<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-container modal-large">
      <div class="modal-header">
        <h3>Add {{ entityTermSingular }}</h3>
        <button @click="close" class="modal-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-message mb-3">{{ error }}</div>

        <!-- Step 1: Search/Filter Input -->
        <div class="form-group">
          <label for="entity-search">
            {{ entityTermSingular }} Name *
          </label>
          <input 
            id="entity-search"
            v-model="searchQuery"
            type="text"
            class="form-control search-input"
            :placeholder="`Start typing ${entityTermSingular.toLowerCase()} name...`"
            @input="handleSearchInput"
            autofocus>
          <small class="form-text text-muted">
            <i class="fas fa-info-circle"></i>
            We'll search existing {{ entityTermPlural.toLowerCase() }} as you type
          </small>
        </div>

        <!-- Loading State -->
        <div v-if="isSearching" class="text-center py-4">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          <p class="text-muted mt-2">Searching...</p>
        </div>

        <!-- Step 2a: Show Filtered Existing Entities (if search query >= 2 chars) -->
        <div v-if="!isSearching && searchQuery.length >= 2 && filteredEntities.length > 0" class="existing-entities-section">
          <div class="section-header">
            <h5>
              <i class="fas fa-search"></i>
              Found {{ filteredEntities.length }} existing {{ filteredEntities.length === 1 ? entityTermSingular.toLowerCase() : entityTermPlural.toLowerCase() }}
            </h5>
            <p class="text-muted small">Click to select</p>
          </div>
          
          <div class="entity-list">
            <div 
              v-for="entity in filteredEntities.slice(0, 10)" 
              :key="entity.id"
              class="entity-card"
              :class="{ 'selected': selectedEntityId === entity.id }"
              @click="selectExistingEntity(entity)">
              <div class="entity-info">
                <div class="entity-name">
                  <strong>{{ entity.name }}</strong>
                  <span v-if="entity.entity_type" class="badge badge-secondary">{{ entity.entity_type }}</span>
                </div>
                <div class="entity-details">
                  <span v-if="entity.primary_email">
                    <i class="fas fa-envelope"></i> {{ entity.primary_email }}
                  </span>
                  <span v-if="entity.address_line1">
                    <i class="fas fa-map-marker-alt"></i> {{ entity.address_line1 }}
                  </span>
                  <span v-if="entity.locations && entity.locations.length > 0">
                    <i class="fas fa-building"></i> {{ entity.locations.length }} location(s)
                  </span>
                </div>
              </div>
              <div class="entity-action">
                <i class="fas fa-check-circle"></i>
              </div>
            </div>
          </div>
          
          <div v-if="filteredEntities.length > 10" class="text-center mt-2">
            <small class="text-muted">
              Showing first 10 of {{ filteredEntities.length }} matches. Keep typing to narrow down.
            </small>
          </div>
          
          <!-- Option to Create New -->
          <div class="create-option mt-3">
            <button @click="startCreatingNew" class="btn btn-outline-secondary btn-block">
              <i class="fas fa-plus"></i>
              Not listed? Create new "{{ searchQuery }}"
            </button>
          </div>
        </div>

        <!-- Step 2b: No Matches Found - Encourage Creation -->
        <div v-if="!isSearching && searchQuery.length >= 3 && filteredEntities.length === 0 && !showCreateForm" class="no-matches-section">
          <div class="empty-state">
            <i class="fas fa-search"></i>
            <h5>No existing {{ entityTermSingular.toLowerCase() }} found matching "{{ searchQuery }}"</h5>
            <p class="text-muted">Let's create a new one!</p>
            <button @click="startCreatingNew" class="btn btn-primary">
              <i class="fas fa-plus"></i>
              Create "{{ searchQuery }}"
            </button>
          </div>
        </div>

        <!-- Step 3: Create New Form (Expanded Inline) -->
        <div v-if="showCreateForm" class="create-form-section">
          <div class="section-divider">
            <span>Create New {{ entityTermSingular }}</span>
          </div>

          <div v-if="entityError" class="alert alert-danger">{{ entityError }}</div>

          <!-- Duplicate Warning (Smart Matching Results) -->
          <div v-if="potentialDuplicates.length > 0" class="duplicate-warning-section">
            <div class="alert alert-warning">
              <div class="warning-header">
                <i class="fas fa-exclamation-triangle"></i>
                <strong>Wait! We found similar {{ entityTermPlural.toLowerCase() }}:</strong>
              </div>
              
              <div class="duplicate-list">
                <div 
                  v-for="match in potentialDuplicates" 
                  :key="match.id"
                  class="duplicate-item">
                  <div class="duplicate-info">
                    <div class="duplicate-name">
                      <strong>{{ match.name }}</strong>
                      <span class="match-badge" :class="getMatchBadgeClass(match.match_type)">
                        {{ getMatchLabel(match.match_type) }}
                      </span>
                      <span class="similarity-badge">{{ Math.round(match.similarity * 100) }}% match</span>
                    </div>
                    <div class="duplicate-details">
                      <span v-if="match.address"><i class="fas fa-map-marker-alt"></i> {{ match.address }}</span>
                      <span v-if="match.primary_email"><i class="fas fa-envelope"></i> {{ match.primary_email }}</span>
                      <span v-if="match.location_count > 0"><i class="fas fa-building"></i> {{ match.location_count }} locations</span>
                    </div>
                  </div>
                  <button 
                    @click="useExistingEntity(match)"
                    class="btn btn-sm btn-primary">
                    <i class="fas fa-check"></i> Use This
                  </button>
                </div>
              </div>
              
              <div class="warning-footer">
                <button 
                  @click="confirmCreateNew"
                  class="btn btn-sm btn-success">
                  <i class="fas fa-plus"></i> Create New Anyway
                </button>
              </div>
            </div>
          </div>

          <form @submit.prevent="createEntity">
            <!-- Basic Information -->
            <div class="form-section">
              <h6 class="section-title">Basic Information</h6>
              
              <div class="row">
                <div class="col-md-8 form-group">
                  <label for="entity-name">{{ entityTermSingular }} Name *</label>
                  <input 
                    type="text" 
                    id="entity-name" 
                    v-model="newEntity.name" 
                    class="form-control"
                    required>
                </div>
                <div class="col-md-4 form-group">
                  <label for="entity-type">Type *</label>
                  <select id="entity-type" v-model="newEntity.entity_type" class="form-control" required>
                    <option value="COMPANY">Company</option>
                    <option value="AGENCY">Agency/Broker</option>
                    <option value="PERSON">Individual</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-6 form-group">
                  <label for="entity-email">Primary Email</label>
                  <input 
                    type="email" 
                    id="entity-email" 
                    v-model="newEntity.primary_email" 
                    class="form-control"
                    placeholder="contact@company.com">
                </div>
                <div class="col-md-6 form-group">
                  <label for="entity-phone">Primary Phone</label>
                  <input 
                    type="tel" 
                    id="entity-phone" 
                    v-model="newEntity.primary_phone" 
                    class="form-control"
                    placeholder="(555) 123-4567">
                </div>
              </div>
              
              <AddressFields
                v-model="newEntity"
                prefix="entity"
              />
            </div>

            <!-- Locations Section -->
            <div class="form-section">
              <h6 class="section-title">
                <i class="fas fa-map-marker-alt"></i>
                Locations / Store Numbers
                <span class="badge badge-light">Optional</span>
              </h6>
              
              <!-- Existing Locations List -->
              <div v-if="locations.length > 0" class="locations-list mb-3">
                <div 
                  v-for="(loc, index) in locations" 
                  :key="index"
                  class="location-card">
                  <div class="location-header">
                    <strong>
                      <span v-if="loc.external_location_id">#{{ loc.external_location_id }}</span>
                      <span v-if="loc.external_location_id && loc.name"> — </span>
                      <span v-if="loc.name">{{ loc.name }}</span>
                      <span v-if="!loc.external_location_id && !loc.name">Location {{ index + 1 }}</span>
                    </strong>
                    <button 
                      type="button"
                      @click="removeLocation(index)"
                      class="btn-icon-remove">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <div v-if="loc.address_line1 || loc.city || loc.state" class="location-address">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ [loc.address_line1, loc.city, loc.state, loc.postal_code].filter(Boolean).join(', ') }}
                  </div>
                </div>
              </div>
              
              <!-- Add Location Button/Form -->
              <button 
                v-if="!showAddLocation"
                type="button"
                @click="showAddLocation = true"
                class="btn btn-sm btn-outline-primary">
                <i class="fas fa-plus"></i> Add Location/Store
              </button>
              
              <div v-if="showAddLocation" class="add-location-form card p-3 mt-2">
                <div class="row">
                  <div class="col-md-6 form-group">
                    <label>Store / Location Number <span class="required-star">*</span></label>
                    <input
                      v-model="newLocation.external_location_id"
                      type="text"
                      class="form-control"
                      placeholder="e.g., 1001A"
                      required>
                    <small class="form-text text-muted">The unique store number or location ID</small>
                  </div>
                  <div class="col-md-6 form-group">
                    <label>Location Name</label>
                    <input
                      v-model="newLocation.name"
                      type="text"
                      class="form-control"
                      placeholder="e.g., Downtown Toronto">
                  </div>
                </div>

                <AddressFields
                  v-model="newLocation"
                  prefix="new-loc"
                />

                <div class="d-flex gap-2 mt-2">
                  <button
                    type="button"
                    @click="addLocation"
                    class="btn btn-sm btn-primary"
                    :disabled="!newLocation.external_location_id">
                    <i class="fas fa-check"></i> Add Location
                  </button>
                  <button
                    type="button"
                    @click="cancelAddLocation"
                    class="btn btn-sm btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- Contacts Section -->
            <div class="form-section">
              <h6 class="section-title">
                <i class="fas fa-user"></i>
                Contacts
                <span class="badge badge-light">Optional</span>
              </h6>
              
              <!-- Contact List -->
              <div v-if="contacts.length > 0" class="contact-list mb-3">
                <div 
                  v-for="(contact, index) in contacts" 
                  :key="index"
                  class="contact-card">
                  <div class="contact-info">
                    <div class="contact-name">
                      <strong>{{ contact.name }}</strong>
                      <span v-if="contact.contact_role" class="badge badge-role">
                        {{ CONTACT_ROLES.find(r => r.value === contact.contact_role)?.label || contact.contact_role }}
                      </span>
                      <span v-if="contact.is_primary" class="badge badge-primary">Primary</span>
                    </div>
                    <div class="contact-details">
                      {{ contact.email }}
                      <span v-if="contact.phone"> • {{ contact.phone }}</span>
                      <span v-if="contact.title"> • {{ contact.title }}</span>
                    </div>
                  </div>
                  <button 
                    type="button"
                    @click="removeContact(index)"
                    class="btn-icon-remove">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
              
              <!-- Add Contact Button/Form -->
              <button 
                v-if="!showAddContact"
                type="button"
                @click="showAddContact = true"
                class="btn btn-sm btn-outline-primary">
                <i class="fas fa-plus"></i> Add Contact
              </button>
              
              <div v-if="showAddContact" class="add-contact-form">
                <div class="row">
                  <div class="col-md-6 form-group">
                    <label>Name <span class="required-star">*</span></label>
                    <input
                      v-model="newContact.name"
                      class="form-control"
                      placeholder="Full name"
                      required>
                  </div>
                  <div class="col-md-6 form-group">
                    <label>Role <span class="required-star">*</span></label>
                    <select v-model="newContact.contact_role" class="form-control" required>
                      <option value="">-- Select Role --</option>
                      <optgroup label="Store / Franchisee">
                        <option v-for="r in CONTACT_ROLES.filter(r => r.group === 'Store / Franchisee')" :key="r.value" :value="r.value">
                          {{ r.label }}
                        </option>
                      </optgroup>
                      <optgroup label="Insurance Agency">
                        <option v-for="r in CONTACT_ROLES.filter(r => r.group === 'Insurance Agency')" :key="r.value" :value="r.value">
                          {{ r.label }}
                        </option>
                      </optgroup>
                      <optgroup label="General">
                        <option v-for="r in CONTACT_ROLES.filter(r => r.group === 'General')" :key="r.value" :value="r.value">
                          {{ r.label }}
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 form-group">
                    <label>Email <span class="required-star">*</span></label>
                    <input
                      v-model="newContact.email"
                      type="email"
                      class="form-control"
                      placeholder="email@example.com"
                      required>
                  </div>
                  <div class="col-md-6 form-group">
                    <label>Phone</label>
                    <input
                      v-model="newContact.phone"
                      type="tel"
                      class="form-control"
                      placeholder="(555) 123-4567">
                  </div>
                </div>
                <div class="form-group">
                  <label>Job Title <small class="text-muted">(optional)</small></label>
                  <input
                    v-model="newContact.title"
                    class="form-control"
                    placeholder="e.g., Regional Manager">
                </div>
                <div class="form-actions">
                  <button
                    type="button"
                    @click="addContact"
                    class="btn btn-sm btn-success"
                    :disabled="!newContact.name || !newContact.email || !newContact.contact_role">
                    <i class="fas fa-check"></i> Add Contact
                  </button>
                  <button
                    type="button"
                    @click="cancelAddContact"
                    class="btn btn-sm btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- Insurance Agents Section -->
            <div class="form-section">
              <h6 class="section-title">
                <i class="fas fa-shield-alt"></i>
                Insurance Agents / Brokers
                <span class="badge badge-light">Optional</span>
              </h6>
              <p class="text-muted small mb-2">Add the insurance agency or broker that handles coverage for this {{ entityTermSingular.toLowerCase() }}.</p>

              <!-- Agents List -->
              <div v-if="agents.length > 0" class="agents-list mb-3">
                <div v-for="(agent, index) in agents" :key="index" class="agent-card-mini">
                  <div class="agent-info-mini">
                    <strong><i class="fas fa-building"></i> {{ agent.agencyName }}</strong>
                    <div v-if="agent.coverageTypes.length > 0" class="coverage-pills-mini mt-1">
                      <span v-for="ct in agent.coverageTypes" :key="ct" class="coverage-pill-mini">{{ ct }}</span>
                    </div>
                    <div v-if="agent.notes" class="text-muted small">{{ agent.notes }}</div>
                  </div>
                  <button type="button" @click="removeAgent(index)" class="btn-icon-remove">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- Add Agent Toggle -->
              <button
                v-if="!showAddAgent"
                type="button"
                @click="showAddAgent = true"
                class="btn btn-sm btn-outline-primary">
                <i class="fas fa-plus"></i> Add Insurance Agent / Agency
              </button>

              <!-- Add Agent Form -->
              <div v-if="showAddAgent" class="add-agent-form card p-3 mt-2">
                <div class="form-group">
                  <label>Insurance Agency / Broker <span class="required-star">*</span></label>
                  <input
                    v-model="newAgent.agencySearchQuery"
                    type="text"
                    class="form-control"
                    placeholder="Search by agency name..."
                    @input="debouncedAgentSearch">
                  <!-- Search results -->
                  <div v-if="newAgent.agencySearchResults.length > 0 && !newAgent.selectedAgency" class="agent-search-results mt-1">
                    <div
                      v-for="e in newAgent.agencySearchResults"
                      :key="e.id"
                      class="agent-search-item"
                      @click="selectAgencyForNew(e)">
                      <strong>{{ e.name }}</strong>
                      <span class="text-muted small ml-2">{{ e.entity_type }}</span>
                    </div>
                  </div>
                  <!-- Create new agency option -->
                  <div
                    v-if="newAgent.agencySearchQuery.length >= 3 && !newAgent.selectedAgency && !newAgent.isSearching && newAgent.agencySearchResults.length === 0"
                    class="create-agency-hint mt-1">
                    <button type="button" @click="createAgencyForNew" class="btn btn-sm btn-outline-secondary">
                      <i class="fas fa-plus"></i> Create agency: <strong>"{{ newAgent.agencySearchQuery }}"</strong>
                    </button>
                  </div>
                  <!-- Selected agency chip -->
                  <div v-if="newAgent.selectedAgency" class="selected-chip mt-1">
                    <i class="fas fa-check-circle text-success"></i>
                    <span class="chip-name">{{ newAgent.selectedAgency.name }}</span>
                    <span class="chip-type">{{ newAgent.selectedAgency.entity_type || 'AGENCY' }}</span>
                    <button type="button" @click="newAgent.selectedAgency = null; newAgent.agencySearchQuery = ''" class="chip-clear">×</button>
                  </div>
                </div>

                <div class="form-group">
                  <label>Coverage Types <small class="text-muted">(leave blank = handles all)</small></label>
                  <div class="coverage-checkboxes">
                    <label v-for="ct in AGENT_COVERAGE_TYPES" :key="ct.value" class="coverage-checkbox-item">
                      <input type="checkbox" :value="ct.value" v-model="newAgent.coverageTypes">
                      {{ ct.label }}
                    </label>
                  </div>
                </div>

                <div class="form-group">
                  <label>Notes <small class="text-muted">(optional)</small></label>
                  <input v-model="newAgent.notes" type="text" class="form-control" placeholder="e.g., primary broker for GL and WC">
                </div>

                <div class="d-flex gap-2 mt-2">
                  <button
                    type="button"
                    @click="addAgent"
                    class="btn btn-sm btn-primary"
                    :disabled="!newAgent.selectedAgency">
                    <i class="fas fa-check"></i> Add Agent
                  </button>
                  <button type="button" @click="cancelAddAgent" class="btn btn-sm btn-secondary">Cancel</button>
                </div>
              </div>
            </div>

            <!-- Form Actions -->
            <div class="form-actions-main">
              <button type="button" @click="cancelCreate" class="btn btn-secondary">
                <i class="fas fa-arrow-left"></i> Back
              </button>
              <button 
                type="submit" 
                class="btn btn-success"
                :disabled="isCreatingEntity || !newEntity.name">
                <span v-if="isCreatingEntity" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="fas fa-plus"></i>
                {{ isCreatingEntity ? 'Creating...' : `Create ${entityTermSingular}` }}
              </button>
            </div>
          </form>
        </div>

      </div>

      <!-- Footer - Only shows when entity is selected -->
      <div v-if="selectedEntityId && !showCreateForm" class="modal-footer">
        <button @click="close" class="btn btn-secondary">Cancel</button>
        <button
          @click="handleAddSubject"
          class="btn btn-primary"
          :disabled="isAddingSubject">
          <span v-if="isAddingSubject" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="fas fa-check"></i>
          {{ isAddingSubject ? 'Adding...' : `Add ${entityTermSingular}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { debounce } from 'lodash-es';
import { CONTACT_ROLES } from '@/constants';
import AddressFields from '@/components/shared/AddressFields.vue';

const AGENT_COVERAGE_TYPES = [
  { value: 'GL',    label: 'General Liability (GL)' },
  { value: 'WC',    label: 'Workers Comp (WC)' },
  { value: 'AUTO',  label: 'Auto Liability' },
  { value: 'UMB',   label: 'Umbrella / Excess' },
  { value: 'PL',    label: 'Professional Liability / E&O' },
  { value: 'EPLI',  label: 'Employment Practices (EPLI)' },
  { value: 'CYBER', label: 'Cyber Liability' },
  { value: 'PROP',  label: 'Property' },
  { value: 'CRIME', label: 'Crime / Fidelity' },
  { value: 'LIQUOR', label: 'Liquor Liability' },
  { value: 'DO',    label: 'Directors & Officers' },
  { value: 'OTHER', label: 'Other' },
];

export default {
  name: 'AddComplianceSubjectModal',
  components: { AddressFields },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
    existingSubjectEntityIds: {
      type: Array,
      default: () => []
    },
    entityTermSingular: {
      type: String,
      default: 'Company'
    },
    entityTermPlural: {
      type: String,
      default: 'Companies'
    }
  },
  data() {
    return {
      // Loading states
      isSearching: false,
      isAddingSubject: false,
      isCreatingEntity: false,
      checkingDuplicates: false,
      
      // Errors
      error: null,
      entityError: null,
      
      // Search and selection
      searchQuery: '',
      filteredEntities: [],
      selectedEntityId: null,
      
      // Create form state
      showCreateForm: false,
      newEntity: {
        name: '',
        entity_type: 'COMPANY',
        primary_email: '',
        primary_phone: '',
        address_line1: ''
      },
      
      // Duplicate detection
      potentialDuplicates: [],
      userConfirmedNew: false,
      
      // Locations
      locations: [],
      showAddLocation: false,
      newLocation: {
        external_location_id: '',
        name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        province: '',
        postal_code: '',
        country: 'US'
      },
      
      // Contacts
      contacts: [],
      showAddContact: false,
      newContact: {
        name: '',
        email: '',
        phone: '',
        contact_role: '',
        title: '',
        is_primary: false
      },

      // Insurance Agents
      agents: [],
      showAddAgent: false,
      newAgent: {
        agencySearchQuery: '',
        agencySearchResults: [],
        selectedAgency: null,
        coverageTypes: [],
        notes: '',
        isSearching: false
      },

      // Constants
      CONTACT_ROLES,
      AGENT_COVERAGE_TYPES,

      // Debounced functions
      debouncedSearch: null,
      debouncedCheckDuplicates: null,
      debouncedAgentSearch: null
    };
  },
  created() {
    this.debouncedSearch = debounce(this.searchEntities, 300);
    this.debouncedCheckDuplicates = debounce(this.checkForDuplicates, 300);
    this.debouncedAgentSearch = debounce(this.searchAgenciesForNew, 300);
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.resetModal();
      }
    },
    'newEntity.name'(newValue) {
      if (newValue && newValue.length >= 3 && this.showCreateForm) {
        this.debouncedCheckDuplicates();
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    
    resetModal() {
      this.searchQuery = '';
      this.filteredEntities = [];
      this.selectedEntityId = null;
      this.showCreateForm = false;
      this.error = null;
      this.entityError = null;
      this.potentialDuplicates = [];
      this.userConfirmedNew = false;
      this.locations = [];
      this.contacts = [];
      this.agents = [];
      this.showAddAgent = false;
      this.bulkLocations = '';
      this.resetNewEntity();
      this.resetNewAgent();
    },
    
    resetNewEntity() {
      this.newEntity = {
        name: '',
        entity_type: 'COMPANY',
        primary_email: '',
        primary_phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        province: '',
        postal_code: '',
        country: 'US'
      };
    },
    
    // Handle search input — triggers debounced live backend search
    handleSearchInput() {
      this.selectedEntityId = null;
      this.filteredEntities = [];
      if (this.searchQuery.length >= 2) {
        this.debouncedSearch();
      }
    },
    
    // Live backend search — replaces local filtering so all entity types are returned globally
    async searchEntities() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.filteredEntities = [];
        return;
      }
      
      this.isSearching = true;
      try {
        const response = await api.get('/entities/', {
          params: { search: this.searchQuery, limit: 10 }
        });
        
        let results = [];
        if (Array.isArray(response.data)) {
          results = response.data;
        } else if (response.data?.results) {
          results = response.data.results;
        }
        
        this.filteredEntities = results.filter(
          entity => !this.existingSubjectEntityIds.includes(entity.id)
        );
      } catch (err) {
        console.error('Error searching entities:', err);
      } finally {
        this.isSearching = false;
      }
    },
    
    // Select an existing entity
    selectExistingEntity(entity) {
      this.selectedEntityId = entity.id;
      this.showCreateForm = false;
      console.log(`Selected existing ${this.entityTermSingular.toLowerCase()}: ${entity.name}`);
    },
    
    // Start creating a new entity
    startCreatingNew() {
      this.showCreateForm = true;
      this.selectedEntityId = null;
      this.newEntity.name = this.searchQuery; // Pre-fill with search query
      
      // Check for duplicates using smart matching
      if (this.newEntity.name.length >= 3) {
        this.checkForDuplicates();
      }
    },
    
    // Cancel create form
    cancelCreate() {
      this.showCreateForm = false;
      this.entityError = null;
      this.potentialDuplicates = [];
      this.userConfirmedNew = false;
      this.locations = [];
      this.contacts = [];
      this.resetNewEntity();
    },
    
    // Check for duplicates using smart matching
    async checkForDuplicates() {
      if (!this.newEntity.name || this.newEntity.name.length < 3) {
        this.potentialDuplicates = [];
        return;
      }
      
      this.checkingDuplicates = true;
      
      try {
        const response = await api.post('/entities/check-duplicates/', {
          name: this.newEntity.name,
          entity_type: this.newEntity.entity_type,
          project_id: this.projectId
        });
        
        this.potentialDuplicates = response.data.matches || [];
        this.userConfirmedNew = false;
        
      } catch (error) {
        console.error('Error checking duplicates:', error);
      } finally {
        this.checkingDuplicates = false;
      }
    },
    
    useExistingEntity(match) {
      this.selectedEntityId = match.id;
      this.showCreateForm = false;
      this.potentialDuplicates = [];
      this.searchQuery = match.name;
      
      // Show the selected entity in results
      const inFiltered = this.filteredEntities.find(e => e.id === match.id);
      if (!inFiltered) {
        this.filteredEntities = [match];
      }
    },
    
    confirmCreateNew() {
      this.userConfirmedNew = true;
      this.potentialDuplicates = [];
    },
    
    getMatchBadgeClass(matchType) {
      return {
        'exact': 'match-exact',
        'fuzzy': 'match-fuzzy',
        'ai_confirmed': 'match-ai'
      }[matchType] || 'match-default';
    },
    
    getMatchLabel(matchType) {
      return {
        'exact': 'Exact Match',
        'fuzzy': 'Similar Name',
        'ai_confirmed': 'Likely Match'
      }[matchType] || 'Possible Match';
    },
    
    // Location management
    addLocation() {
      if (!this.newLocation.external_location_id.trim()) {
        alert('Store number is required');
        return;
      }
      
      // Check for duplicate store number
      const duplicate = this.locations.find(loc => 
        loc.external_location_id === this.newLocation.external_location_id
      );
      
      if (duplicate) {
        alert('A location with this store number already exists');
        return;
      }
      
      this.locations.push({ ...this.newLocation });
      this.cancelAddLocation();
    },
    
    cancelAddLocation() {
      this.showAddLocation = false;
      this.newLocation = {
        external_location_id: '',
        name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        province: '',
        postal_code: '',
        country: 'US'
      };
    },
    
    removeLocation(index) {
      this.locations.splice(index, 1);
    },
    
    // Contact management
    addContact() {
      if (!this.newContact.name || !this.newContact.email) return;
      
      if (this.contacts.length === 0) {
        this.newContact.is_primary = true;
      }
      
      this.contacts.push({ ...this.newContact });
      this.cancelAddContact();
    },
    
    cancelAddContact() {
      this.showAddContact = false;
      this.newContact = {
        name: '',
        email: '',
        phone: '',
        contact_role: '',
        title: '',
        is_primary: false
      };
    },
    
    removeContact(index) {
      this.contacts.splice(index, 1);
      
      if (this.contacts.length > 0 && !this.contacts.some(c => c.is_primary)) {
        this.contacts[0].is_primary = true;
      }
    },

    // Insurance Agent management
    resetNewAgent() {
      this.newAgent = {
        agencySearchQuery: '',
        agencySearchResults: [],
        selectedAgency: null,
        coverageTypes: [],
        notes: '',
        isSearching: false
      };
    },

    cancelAddAgent() {
      this.showAddAgent = false;
      this.resetNewAgent();
    },

    async searchAgenciesForNew() {
      const q = this.newAgent.agencySearchQuery;
      if (!q || q.length < 2) {
        this.newAgent.agencySearchResults = [];
        return;
      }
      this.newAgent.isSearching = true;
      try {
        const response = await api.get('/entities/', { params: { search: q, limit: 8 } });
        const data = response.data;
        this.newAgent.agencySearchResults = Array.isArray(data) ? data : (data.results || []);
      } catch (e) {
        this.newAgent.agencySearchResults = [];
      } finally {
        this.newAgent.isSearching = false;
      }
    },

    selectAgencyForNew(entity) {
      this.newAgent.selectedAgency = entity;
      this.newAgent.agencySearchQuery = entity.name;
      this.newAgent.agencySearchResults = [];
    },

    async createAgencyForNew() {
      const name = this.newAgent.agencySearchQuery.trim();
      if (!name) return;
      try {
        const response = await api.post('/entities/', { name, entity_type: 'AGENCY' });
        this.newAgent.selectedAgency = response.data;
        this.newAgent.agencySearchQuery = response.data.name;
        this.newAgent.agencySearchResults = [];
      } catch (e) {
        console.error('Error creating agency:', e);
      }
    },

    addAgent() {
      if (!this.newAgent.selectedAgency) return;
      this.agents.push({
        agencyId: this.newAgent.selectedAgency.id,
        agencyName: this.newAgent.selectedAgency.name,
        coverageTypes: [...this.newAgent.coverageTypes],
        notes: this.newAgent.notes
      });
      this.showAddAgent = false;
      this.resetNewAgent();
    },

    removeAgent(index) {
      this.agents.splice(index, 1);
    },
    
    // Create new entity with locations and contacts
    async createEntity() {
      if (!this.newEntity.name.trim()) {
        this.entityError = `${this.entityTermSingular} name is required.`;
        return;
      }
      
      // Check if user needs to confirm duplicate creation
      if (this.potentialDuplicates.length > 0 && !this.userConfirmedNew) {
        this.entityError = `Please select an existing ${this.entityTermSingular.toLowerCase()} or click "Create New Anyway" to confirm.`;
        return;
      }
      
      this.isCreatingEntity = true;
      this.entityError = null;
      
      try {
        const response = await api.post('/entities/create-with-details/', {
          entity: {
            ...this.newEntity,
            project: this.projectId
          },
          locations: this.locations,
          contacts: this.contacts,
          force_create: this.userConfirmedNew
        });
        
        console.log(`${this.entityTermSingular} created:`, response.data);
        
        // Entity created — immediately add as compliance subject without showing it as
        // a "search result" first, which was confusing users into thinking it was a duplicate.
        this.selectedEntityId = response.data.entity.id;
        this.showCreateForm = false;

        const locationMsg = response.data.locations_created > 0
          ? ` with ${response.data.locations_created} location(s)`
          : '';
        const contactMsg = response.data.contacts_created > 0
          ? ` and ${response.data.contacts_created} contact(s)`
          : '';
        console.log(`Created "${response.data.entity.name}"${locationMsg}${contactMsg} — adding as subject...`);

        // Auto-commit any partially filled agent draft the user may not have clicked "Add Agent" on
        if (this.showAddAgent && this.newAgent.selectedAgency) {
          this.addAgent();
        }

        // Proceed directly to adding as compliance subject
        await this.handleAddSubject();
        
      } catch (err) {
        console.error('Error creating entity:', err);
        
        if (err.response && err.response.data) {
          if (typeof err.response.data === 'string') {
            this.entityError = err.response.data;
          } else if (err.response.data.error) {
            this.entityError = err.response.data.error;
          } else if (err.response.data.detail) {
            this.entityError = err.response.data.detail;
          } else {
            this.entityError = `Failed to create ${this.entityTermSingular.toLowerCase()}. Please try again.`;
          }
        } else {
          this.entityError = `Failed to create ${this.entityTermSingular.toLowerCase()}. ${err.message || ''}`;
        }
      } finally {
        this.isCreatingEntity = false;
      }
    },
    
    // Add selected entity as compliance subject
    async handleAddSubject() {
      if (!this.selectedEntityId) return;

      this.isAddingSubject = true;
      this.error = null;
      
      try {
        const payload = {
          project: this.projectId,
          subject_entity: this.selectedEntityId
        };
        
        const response = await api.post('/compliance-subjects/', payload);
        const subjectId = response.data.id;
        console.log(`Added ${this.entityTermSingular.toLowerCase()} as compliance subject:`, response.data);

        // Save any collected insurance agents
        const agentFailures = [];
        for (const agent of this.agents) {
          try {
            await api.post(`/compliance-subjects/${subjectId}/agents/`, {
              agent_entity_id: agent.agencyId,
              coverage_types: agent.coverageTypes,
              notes: agent.notes
            });
          } catch (agentErr) {
            console.error(`Failed to save agent ${agent.agencyName}:`, agentErr);
            agentFailures.push(agent.agencyName);
          }
        }

        this.$emit('subject-added', response.data);

        // Show a visible warning if any agents failed — don't silently drop them
        if (agentFailures.length > 0) {
          this.error = `${this.entityTermSingular} was added, but the following agent(s) could not be saved and will need to be added manually: ${agentFailures.join(', ')}`;
          // Don't close — let user see the error
        } else {
          this.close();
        }
        
      } catch (err) {
        console.error('Error adding compliance subject:', err);
        
        if (err.response && err.response.data) {
          if (typeof err.response.data === 'string') {
            this.error = err.response.data;
          } else if (err.response.data.non_field_errors) {
            this.error = err.response.data.non_field_errors[0];
          } else if (err.response.data.detail) {
            this.error = err.response.data.detail;
          } else {
            this.error = `Failed to add ${this.entityTermSingular.toLowerCase()}. Please try again.`;
          }
        } else {
          this.error = `Failed to add ${this.entityTermSingular.toLowerCase()}. ${err.message || ''}`;
        }
      } finally {
        this.isAddingSubject = false;
      }
    }
  }
};
</script>

<style scoped>
/* Modal Base */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-large {
  max-width: 800px;
}

.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.75rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #000;
}

.modal-body {
  padding: 25px;
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer {
  padding: 15px 25px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

/* Search Input */
.search-input {
  font-size: 1.1rem;
  padding: 12px;
  border: 2px solid #ced4da;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-color: #0d6efd;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

/* Entity List */
.existing-entities-section {
  margin-top: 20px;
}

.section-header h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}

.entity-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 15px;
  max-height: 400px;
  overflow-y: auto;
}

.entity-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #fff;
}

.entity-card:hover {
  border-color: #0d6efd;
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.entity-card.selected {
  border-color: #0d6efd;
  background-color: #e7f1ff;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
}

.entity-info {
  flex-grow: 1;
}

.entity-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.entity-name strong {
  font-size: 1.05rem;
  color: #2c3e50;
}

.entity-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  font-size: 0.9rem;
  color: #6c757d;
}

.entity-action {
  color: #ced4da;
  font-size: 1.5rem;
  transition: color 0.2s;
}

.entity-card.selected .entity-action {
  color: #0d6efd;
}

/* Empty State */
.no-matches-section {
  margin-top: 30px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-state i {
  font-size: 3rem;
  color: #ced4da;
  margin-bottom: 15px;
}

.empty-state h5 {
  color: #495057;
  margin-bottom: 10px;
}

/* Create Form */
.create-form-section {
  margin-top: 20px;
}

.section-divider {
  text-align: center;
  margin: 20px 0;
  position: relative;
}

.section-divider span {
  background-color: #fff;
  padding: 0 15px;
  color: #495057;
  font-weight: 600;
  font-size: 1.1rem;
  position: relative;
  z-index: 1;
}

.section-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #dee2e6;
}

.form-section {
  margin-bottom: 25px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Duplicate Warning */
.duplicate-warning-section {
  margin-bottom: 20px;
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
}

.duplicate-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 15px 0;
}

.duplicate-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.duplicate-info {
  flex-grow: 1;
}

.duplicate-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.duplicate-details {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.85rem;
  color: #6c757d;
}

.match-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.match-exact {
  background-color: #dc3545;
  color: white;
}

.match-fuzzy {
  background-color: #ffc107;
  color: #000;
}

.match-ai {
  background-color: #0dcaf0;
  color: #000;
}

.similarity-badge {
  background-color: #e9ecef;
  color: #495057;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.warning-footer {
  margin-top: 15px;
  text-align: center;
}

/* Location Chips */
/* Location Cards */
.locations-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.location-card {
  padding: 12px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.location-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.location-address {
  font-size: 0.9rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 6px;
}

.add-location-form {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.add-location-form .d-flex {
  display: flex;
}

.add-location-form .gap-2 {
  gap: 0.5rem;
}

/* Contact Cards */
.contact-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.contact-info {
  flex-grow: 1;
}

.contact-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.contact-details {
  font-size: 0.9rem;
  color: #6c757d;
}

.btn-icon-remove {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 5px;
  transition: opacity 0.2s;
}

.btn-icon-remove:hover {
  opacity: 0.7;
}

.add-contact-form {
  padding: 15px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-top: 10px;
}

/* Form Elements */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
}

.form-control {
  display: block;
  width: 100%;
  padding: 10px 12px;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #0d6efd;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.form-text {
  display: block;
  margin-top: 5px;
  font-size: 0.875rem;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-primary {
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0b5ed7;
  border-color: #0a58ca;
}

.btn-secondary {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5c636a;
  border-color: #565e64;
}

.btn-success {
  color: #fff;
  background-color: #198754;
  border-color: #198754;
}

.btn-success:hover:not(:disabled) {
  background-color: #157347;
  border-color: #146c43;
}

.btn-outline-primary {
  color: #0d6efd;
  border-color: #0d6efd;
  background-color: transparent;
}

.btn-outline-primary:hover:not(:disabled) {
  color: #fff;
  background-color: #0d6efd;
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
  background-color: transparent;
}

.btn-outline-secondary:hover:not(:disabled) {
  color: #fff;
  background-color: #6c757d;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.875rem;
}

.btn-block {
  display: block;
  width: 100%;
}

/* Badges */
.badge {
  display: inline-block;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  border-radius: 4px;
}

.badge-primary {
  background-color: #0d6efd;
  color: white;
}

.badge-role {
  background-color: #6610f2;
  color: white;
}

.required-star {
  color: #dc3545;
  margin-left: 2px;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.badge-light {
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

/* Alerts */
.alert {
  padding: 12px 16px;
  margin-bottom: 15px;
  border: 1px solid transparent;
  border-radius: 6px;
}

.alert-info {
  color: #055160;
  background-color: #cff4fc;
  border-color: #b6effb;
}

.alert-warning {
  color: #664d03;
  background-color: #fff3cd;
  border-color: #ffecb5;
}

.alert-danger {
  color: #842029;
  background-color: #f8d7da;
  border-color: #f5c2c7;
}

/* Utilities */
.text-muted {
  color: #6c757d !important;
}

.text-center {
  text-align: center !important;
}

.mb-1 { margin-bottom: 0.25rem !important; }
.mb-2 { margin-bottom: 0.5rem !important; }
.mb-3 { margin-bottom: 1rem !important; }
.mt-2 { margin-top: 0.5rem !important; }
.mt-3 { margin-top: 1rem !important; }
.me-1 { margin-right: 0.25rem !important; }
.ms-2 { margin-left: 0.5rem !important; }
.py-3 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
.py-4 { padding-top: 1.5rem !important; padding-bottom: 1.5rem !important; }

.d-inline-block { display: inline-block !important; }

.small {
  font-size: 0.875rem;
}

/* Grid System */
.row {
  display: flex;
  flex-wrap: wrap;
  margin-right: -10px;
  margin-left: -10px;
}

.col-md-4,
.col-md-6,
.col-md-8 {
  padding-right: 10px;
  padding-left: 10px;
}

.col-md-4 { flex: 0 0 33.333333%; max-width: 33.333333%; }
.col-md-6 { flex: 0 0 50%; max-width: 50%; }
.col-md-8 { flex: 0 0 66.666667%; max-width: 66.666667%; }

/* Form Actions */
.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.form-actions-main {
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

/* Spinners */
.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    max-width: 95%;
    max-height: 95vh;
  }
  
  .col-md-4,
  .col-md-6,
  .col-md-8 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  .entity-details {
    flex-direction: column;
    gap: 5px;
  }
  
  .form-actions-main {
    flex-direction: column;
  }
}

/* Insurance Agent section */
.agent-card-mini {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 6px;
}

.agent-info-mini {
  flex: 1;
  font-size: 0.9rem;
}

.coverage-pills-mini {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.coverage-pill-mini {
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 0.72rem;
  font-weight: 600;
}

.add-agent-form {
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.agent-search-results {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  max-height: 160px;
  overflow-y: auto;
  background: #fff;
}

.agent-search-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.88rem;
  transition: background 0.15s;
}

.agent-search-item:hover {
  background: #f0f4ff;
}

.create-agency-hint {
  font-size: 0.85rem;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.85rem;
}

.chip-name { font-weight: 600; }

.chip-type {
  background: #c8e6c9;
  color: #2e7d32;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
}

.chip-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: #777;
  font-size: 1rem;
  padding: 0;
  line-height: 1;
}

.chip-clear:hover { color: #333; }

.coverage-checkboxes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-top: 4px;
}

.coverage-checkbox-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: normal;
  cursor: pointer;
}

.coverage-checkbox-item input[type="checkbox"] {
  width: auto;
  margin: 0;
}
</style>
